<template>
	<div
		class="max-w-2xl mx-auto p-4 md:p-6 bg-white rounded-xl md:rounded-2xl shadow-lg border border-gray-100 transition-all duration-300 animate-fade-in-up"
	>
		<!-- Header Section -->
		<div class="mb-6 md:mb-8 text-center">
			<div
				class="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 mb-3 md:mb-4 bg-blue-50 rounded-xl md:rounded-2xl"
			>
				<i class="fas fa-calendar-alt text-blue-600"></i>
			</div>
			<h2
				class="mb-2 md:mb-3 text-xl md:text-2xl font-bold text-gray-900"
			>
				Cover Details
			</h2>
			<p class="text-sm md:text-base text-gray-600 px-2 md:px-0">
				Choose your coverage duration and when you'd like your
				protection to begin
			</p>
		</div>

		<!-- Form Section -->
		<form @submit.prevent="submitDetails" class="space-y-4 md:space-y-6">
			<!-- Cover Period Field -->
			<div class="space-y-2">
				<label
					for="coverPeriod"
					class="block text-sm font-medium text-gray-700"
				>
					Cover Period
				</label>
				<div class="relative">
					<select
						id="coverPeriod"
						v-model="coverDetails.coverPeriod"
						required
						class="w-full px-4 py-3.5 text-gray-900 border border-gray-300 rounded-lg md:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 appearance-none bg-white text-base md:text-inherit"
					>
						<option
							disabled
							value=""
							:selected="!coverDetails.coverPeriod"
						>
							Select period
						</option>
						<option
							v-for="period in product.productCover
								.supportedPeriods"
							:key="period"
							:value="period"
						>
							{{ formatPeriod(period) }}
						</option>
					</select>
					<div
						class="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none"
					>
						<i class="fas fa-chevron-down text-gray-400"></i>
					</div>
				</div>
				<p class="text-xs md:text-sm text-gray-500">
					Select how long you need coverage
				</p>
			</div>

			<!-- Start Date Field -->
			<div class="space-y-2">
				<label
					for="startDate"
					class="block text-sm font-medium text-gray-700"
				>
					Start Date
				</label>
				<input
					type="date"
					id="startDate"
					v-model="coverDetails.startDate"
					required
					:min="today"
					class="w-full px-4 py-3.5 text-gray-900 border border-gray-300 rounded-lg md:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 bg-white text-base md:text-inherit"
				/>
				<p class="text-xs md:text-sm text-gray-500">
					When should your coverage begin?
				</p>
			</div>

			<div class="flex flex-col md:flex-row gap-3 md:gap-4 pt-4 md:pt-6">
				<button
					type="button"
					@click="previous"
					class="w-full md:flex-1 py-3.5 px-4 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg md:rounded-xl hover:bg-gray-200 transition-colors duration-200 font-medium flex items-center justify-center text-base order-2 sm:order-1"
				>
					<i class="fas fa-arrow-left mr-2"></i>
					Back
				</button>
				<button
					type="submit"
					class="w-full md:flex-1 py-3.5 px-4 text-white bg-blue-600 rounded-lg md:rounded-xl hover:bg-blue-700 transition-colors duration-200 font-medium flex items-center justify-center shadow-md hover:shadow-lg text-base order-1 sm:order-2"
				>
					Continue
					<i class="fas fa-arrow-right ml-2"></i>
				</button>
			</div>
		</form>
	</div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';

export default {
	name: 'CoverDetails',
	data() {
		return {
			today: new Date().toISOString().split('T')[0],
		};
	},
	computed: {
		...mapState('insurance/motorPrivate', ['coverDetails']),
		...mapGetters('product', ['selectedProduct', 'selectedCoverType']),
		product() {
			return this.selectedProduct;
		},
		selectedCoverType() {
			return this.$store.getters['product/selectedCoverType'];
		},
	},
	methods: {
		...mapMutations('insurance/motorPrivate', [
			'UPDATE_COVER_DETAILS',
			'SET_ERRORS',
			'CLEAR_ERRORS',
		]),
		formatPeriod(period) {
			switch (period) {
				case 1:
					return '1 Day Coverage';
				case 30:
					return '1 Month Coverage';
				case 180:
					return '6 Months Coverage';
				case 360:
					return '12 Months Coverage';
				default:
					return `${period} Days Coverage`;
			}
		},
		async submitDetails() {
			if (this.coverDetails.coverPeriod && this.coverDetails.startDate) {
				this.$emit('cover-details-completed');
				this.$store.dispatch('quotation/setQuotationData', {
					period: this.coverDetails.coverPeriod,
					start_date: this.coverDetails.startDate,
				});
				this.$router.push({
					name:
						this.selectedCoverType === 'third-party'
							? 'Quotation'
							: 'BenefitsPage',
				});
				Event.$emit('notifications:show', {
					type: 'success',
					title: 'Cover Details Submitted',
					message:
						'Your cover details have been successfully submitted.',
					duration: 3000,
				});
			} else {
				this.SET_ERRORS({
					coverPeriod: !this.coverDetails.coverPeriod
						? 'Cover period is required.'
						: '',
					startDate: !this.coverDetails.startDate
						? 'Start date is required.'
						: '',
				});
			}
		},
		previous() {
			this.$router.push({ name: 'ProductQuotation' });
		},
		mounted() {
			console.log('product', this.product);
		},
	},
};
</script>

<style scoped>
@keyframes fade-in-up {
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}
.animate-fade-in-up {
	animation: fade-in-up 0.5s ease-out forwards;
}

select,
input[type='date'] {
	min-height: 48px;
}

input[type='date'] {
	position: relative;
}

input[type='date']::-webkit-calendar-picker-indicator {
	background: transparent;
	bottom: 0;
	color: transparent;
	cursor: pointer;
	height: auto;
	left: 0;
	position: absolute;
	right: 0;
	top: 0;
	width: auto;
}

@media (max-width: 768px) {
	.max-w-2xl {
		max-width: 100%;
		margin-left: 0.5rem;
		margin-right: 0.5rem;
	}

	.p-4 {
		padding: 1.25rem;
	}

	button {
		min-height: 48px;
	}
}

@media screen and (max-width: 768px) {
	select,
	input[type='date'] {
		font-size: 16px;
	}
}

@media (prefers-reduced-motion: reduce) {
	.animate-fade-in-up {
		animation: none;
	}
	.transition-all {
		transition: none;
	}
}
</style>
